import {
    MutableRefObject,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import api from 'src/api/api';
import { HEADERS } from 'src/constants/headers';
import {
    TAssistantMessage,
    TAssistantMessageKind,
} from 'src/modules/assistant/types/message';
import { MESSAGE_KIND, MESSAGE_ROLE, NEXT_ACTION_PROMPT } from '../constants';
import { getChatUrl } from '../helpers/getChatUrl';
import { getRequestErrorKey } from '../helpers/getRequestErrorKey';
import { useActionRecording } from './useActionRecording';

const SEND_MESSAGE_URL = '/ai/chat';
const PENDING_ID_PREFIX = 'pending-';

interface UseAssistantChatParams {
    scopeKey: string;
    scopeKeyRef: MutableRefObject<string>;
    contextLeadId: string | null;
    activeStoreId: string | null;
    canUseAssistant: boolean;
}

interface TChatResponse {
    data: { messages?: TAssistantMessage[] };
}

/**
 * Chat of the active scope: history, messages, next best action and the
 * recording of actions/feedback. Late responses for a previous scope are ignored.
 */
export const useAssistantChat = ({
    scopeKey,
    scopeKeyRef,
    contextLeadId,
    activeStoreId,
    canUseAssistant,
}: UseAssistantChatParams) => {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<TAssistantMessage[]>([]);
    const [loadedScope, setLoadedScope] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const loading = loadedScope !== scopeKey;
    const isCurrent = useCallback(
        (requestScope: string) => scopeKeyRef.current === requestScope,
        [scopeKeyRef]
    );
    const historyUrl = getChatUrl({
        leadId: contextLeadId,
        storeId: activeStoreId,
    });

    useEffect(() => {
        let active = true;
        setMessages([]);
        setLoadedScope(null);
        setError('');
        if (!canUseAssistant) {
            setLoadedScope(scopeKey);
            return () => {
                active = false;
            };
        }
        api.get(historyUrl, HEADERS())
            .then((response) => {
                if (!active) return;
                setMessages(
                    (response.data as TChatResponse).data.messages || []
                );
                setLoadedScope(scopeKey);
            })
            .catch((requestError) => {
                if (!active) return;
                setError(
                    t(getRequestErrorKey(requestError, 'Assistant.ErrorLoad'))
                );
                setLoadedScope(scopeKey);
            });
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStoreId, canUseAssistant, scopeKey]);

    /**
     * Sends a request with an optimistic user message; on failure restores the
     * history from the server.
     */
    const runRequest = useCallback(
        async (
            kind: TAssistantMessageKind,
            content: string,
            request: () => Promise<{ data: { data: TAssistantMessage[] } }>,
            fallbackErrorKey: string
        ) => {
            const requestScope = scopeKey;
            const requestHistoryUrl = historyUrl;
            const temporaryId = `${PENDING_ID_PREFIX}${Date.now()}`;
            setMessages((previous) => [
                ...previous,
                { _id: temporaryId, role: MESSAGE_ROLE.USER, kind, content },
            ]);
            setBusy(true);
            setError('');
            try {
                const response = await request();
                if (isCurrent(requestScope))
                    setMessages((previous) => [
                        ...previous.filter(
                            (entry) => entry._id !== temporaryId
                        ),
                        ...response.data.data,
                    ]);
            } catch (requestError) {
                if (!isCurrent(requestScope)) return;
                setMessages((previous) =>
                    previous.filter((entry) => entry._id !== temporaryId)
                );
                setError(t(getRequestErrorKey(requestError, fallbackErrorKey)));
                api.get(requestHistoryUrl, HEADERS())
                    .then((response) => {
                        if (isCurrent(requestScope))
                            setMessages(
                                (response.data as TChatResponse).data
                                    .messages || []
                            );
                    })
                    .catch(() => undefined);
            } finally {
                setBusy(false);
            }
        },
        [historyUrl, isCurrent, scopeKey, t]
    );

    const sendMessage = useCallback(
        async (text: string) => {
            const content = text.trim();
            if (!content || busy || loading || !canUseAssistant) return;
            await runRequest(
                MESSAGE_KIND.TEXT,
                content,
                () =>
                    api.post(
                        SEND_MESSAGE_URL,
                        {
                            leadId: contextLeadId,
                            message: content,
                            ...(!contextLeadId && { storeId: activeStoreId }),
                        },
                        HEADERS()
                    ),
                'Assistant.ErrorSend'
            );
        },
        [
            activeStoreId,
            busy,
            canUseAssistant,
            contextLeadId,
            loading,
            runRequest,
        ]
    );

    const determineNextAction = useCallback(async () => {
        if (!contextLeadId || busy || loading || !canUseAssistant) return;
        await runRequest(
            MESSAGE_KIND.NEXT_ACTION,
            NEXT_ACTION_PROMPT,
            () =>
                api.post(
                    `/ai/leads/${contextLeadId}/next-action`,
                    {},
                    HEADERS()
                ),
            'Assistant.ErrorAnalyze'
        );
    }, [busy, canUseAssistant, contextLeadId, loading, runRequest]);

    const { completeAssistantAction, rateActionSuggestion } =
        useActionRecording({
            scopeKey,
            contextLeadId,
            canUseAssistant,
            isCurrent,
            setMessages,
            setError,
        });

    return useMemo(
        () => ({
            messages: loading ? [] : messages,
            busy,
            loading,
            error,
            sendMessage,
            determineNextAction,
            completeAssistantAction,
            rateActionSuggestion,
        }),
        [
            busy,
            completeAssistantAction,
            determineNextAction,
            error,
            loading,
            messages,
            rateActionSuggestion,
            sendMessage,
        ]
    );
};
